/**
 * customerProfileFields — validating what staff type into a customer record. PURE.
 *
 * A customer record is the backbone the whole ERP reads from, and these fields reach
 * invoices, birthday campaigns and the delivery label. So the rules are strict about the
 * things that break something downstream (a malformed email silently fails every invoice
 * send; a mistyped year makes a 2-year-old customer) and relaxed about the rest — a name
 * is whatever the person says it is.
 *
 * Nothing here writes. The caller saves only what `validateProfile` returns clean.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MIN_AGE_YEARS = 10;      // below this it is almost certainly a typo
const MAX_AGE_YEARS = 110;

const str = (v) => String(v == null ? '' : v).trim();

/** YYYY-MM-DD in IST, for comparing a date of birth against today. */
export const istToday = (now = new Date()) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(now);

/**
 * Validate the editable identity fields.
 * @returns {{ok: boolean, field?: string, error?: string, values?: object}}
 */
export function validateProfile(input = {}, { today = istToday() } = {}) {
    const fail = (field, error) => ({ ok: false, field, error });

    const name = str(input.name).slice(0, 80);
    if (name && name.length < 2) return fail('name', 'A name needs at least two characters.');

    // Optional, but if it is there it must work — a wrong address means every invoice and
    // order update to this customer fails silently.
    const email = str(input.email).toLowerCase().slice(0, 120);
    if (email && !EMAIL_RE.test(email)) return fail('email', 'Check the email address, or leave it blank.');

    const dob = str(input.dateOfBirth).slice(0, 10);
    if (dob) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return fail('dateOfBirth', 'Use the date picker to set the date of birth.');
        const t = Date.parse(`${dob}T00:00:00+05:30`);
        if (!Number.isFinite(t)) return fail('dateOfBirth', 'That is not a real date.');
        if (dob > today) return fail('dateOfBirth', 'A date of birth cannot be in the future.');
        const years = yearsBetween(dob, today);
        if (years > MAX_AGE_YEARS) return fail('dateOfBirth', 'Check the year — that date is over 110 years ago.');
        if (years < MIN_AGE_YEARS) return fail('dateOfBirth', `Check the year — that would make them ${years} years old.`);
    }

    // A second number is for reaching them, so it has to be a real mobile or nothing.
    const alt = str(input.secondaryMobile).replace(/\D/g, '').slice(-10);
    if (str(input.secondaryMobile) && !/^[6-9]\d{9}$/.test(alt)) {
        return fail('secondaryMobile', 'Enter a valid 10-digit alternate mobile, or leave it blank.');
    }
    if (alt && alt === str(input.primaryMobile).replace(/\D/g, '').slice(-10)) {
        return fail('secondaryMobile', 'The alternate number is the same as the main one.');
    }

    return {
        ok: true,
        values: {
            name,
            email,
            dateOfBirth: dob,
            secondaryMobile: alt,
            gender: ['male', 'female', 'other', ''].includes(str(input.gender).toLowerCase()) ? str(input.gender).toLowerCase() : '',
            notes: str(input.notes).slice(0, 500),
        },
    };
}

/** Whole years between two YYYY-MM-DD dates. */
export function yearsBetween(from, to) {
    const [y1, m1, d1] = String(from).split('-').map(Number);
    const [y2, m2, d2] = String(to).split('-').map(Number);
    if (!y1 || !y2) return 0;
    let years = y2 - y1;
    if (m2 < m1 || (m2 === m1 && d2 < d1)) years -= 1;
    return years;
}

/** Age today, or null when there is no date of birth. */
export const ageFrom = (dob, today = istToday()) => (dob ? yearsBetween(dob, today) : null);

/**
 * Days until the next birthday — what a birthday campaign actually selects on.
 * Returns 0 on the day itself, and null when there is no date of birth.
 */
export function daysToBirthday(dob, today = istToday()) {
    if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(String(dob))) return null;
    const [, m, d] = String(dob).split('-').map(Number);
    const [ty, tm, td] = String(today).split('-').map(Number);
    const at = (y) => Date.UTC(y, m - 1, d);
    const todayUtc = Date.UTC(ty, tm - 1, td);
    // 29 February in a non-leap year is marked on the 28th rather than skipped.
    const safe = (y) => (m === 2 && d === 29 && !isLeap(y) ? Date.UTC(y, 1, 28) : at(y));
    let next = safe(ty);
    if (next < todayUtc) next = safe(ty + 1);
    return Math.round((next - todayUtc) / 86400000);
}

const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

/** What a photo may be. Kept small — this is a thumbnail on a profile, not an album. */
export const PHOTO = { maxBytes: 5 * 1024 * 1024, types: ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] };

export function validatePhoto(file) {
    if (!file) return { ok: false, error: 'Choose an image.' };
    if (!PHOTO.types.includes(String(file.type).toLowerCase())) {
        return { ok: false, error: 'Use a JPG, PNG or WebP image.' };
    }
    if (file.size > PHOTO.maxBytes) {
        return { ok: false, error: `That image is ${(file.size / 1048576).toFixed(1)} MB — keep it under 5 MB.` };
    }
    return { ok: true };
}

/** One delivery address, checked the way the courier will read it. */
export function validateAddress(a = {}) {
    const fail = (field, error) => ({ ok: false, field, error });
    const line = str(a.fullAddress || a.address).slice(0, 300);
    if (line.length < 10) return fail('fullAddress', 'Write the full address — building, street and area.');

    const country = str(a.country).slice(0, 60) || 'India';
    const pincode = str(a.pincode).replace(/[^A-Za-z0-9 -]/g, '').slice(0, 10);
    if (/^india$/i.test(country) && !/^\d{6}$/.test(pincode)) {
        return fail('pincode', 'An Indian pincode is 6 digits.');
    }
    if (!/^india$/i.test(country) && !pincode) return fail('pincode', 'Enter the postal or ZIP code.');

    return {
        ok: true,
        values: {
            fullAddress: line,
            city: str(a.city).slice(0, 60),
            state: str(a.state).slice(0, 60),
            pincode,
            country,
            landmark: str(a.landmark).slice(0, 120),
            label: str(a.label).slice(0, 20),
        },
    };
}

/** Only the fields that actually changed — an audit line should not list untouched ones. */
export function changedFields(before = {}, after = {}) {
    const out = {};
    for (const [k, v] of Object.entries(after)) {
        const had = before[k] == null ? '' : before[k];
        if (String(had) !== String(v == null ? '' : v)) out[k] = { from: had, to: v };
    }
    return out;
}
